import type { QueryClient } from '@tanstack/react-query'

/**
 * Query-key roots that can be made stale by an incoming push notification.
 *
 * When another user shares/updates a calendar event (or anything that triggers
 * a server-side notification), no local mutation fires in this app, so the
 * usual mutation `onSuccess` invalidations never run and the lists stay cached
 * for up to `staleTime`. Invalidating these roots on push keeps the agenda and
 * notifications screens in sync. Mirrors the keys invalidated by the
 * calendar-event and notification mutations.
 *
 * A "new message" push also refreshes the conversation list (and its unread
 * badge, whose key starts with 'conversations') and the messages of that
 * conversation: see `invalidateQueriesOnPush`.
 */
export const REFRESHABLE_ON_PUSH_QUERY_KEYS: ReadonlyArray<string> = [
  'notifications',
  'calendar-events',
]

/**
 * Invalidate the lists a push notification can make stale so the UI refetches
 * instead of showing data up to `staleTime` old.
 */
export const invalidateQueriesOnPush = (
  queryClient: QueryClient,
  data?: { [key: string]: unknown } | null,
): void => {
  REFRESHABLE_ON_PUSH_QUERY_KEYS.forEach((key) => {
    void queryClient.invalidateQueries({ queryKey: [key] })
  })

  // A message from someone else: without this the open conversation and the
  // list keep showing the old content until the next poll.
  if (data?.type !== 'NEW_MESSAGE') return
  void queryClient.invalidateQueries({ queryKey: ['conversations'] })
  if (typeof data.conversationId === 'string' && data.conversationId) {
    void queryClient.invalidateQueries({ queryKey: [`messages-${data.conversationId}`] })
  }
}
